/* eslint-disable @typescript-eslint/ban-ts-comment */
//@ts-nocheck
import { useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import useImage from 'use-image';

import { Carousel } from '@mantine/carousel';
import { ActionIcon, Box, Flex, Skeleton } from '@mantine/core';
import { randomId } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { PlaceMapPath } from '@/entities/PlaceMapPath/PlaceMapPath';

import { bookingObjectsApi } from '@/shared/api/bookingObjects.ts';
import { ApiRoutes, BASE_API_URL } from '@/shared/constant/api';
import { getImageScale, getMembers } from '@/shared/helpers/utils.ts';
import useAppStore from '@/shared/store/app.store.ts';
import useBookingStore from '@/shared/store/booking.store.ts';
import useUserStore from '@/shared/store/user.store.ts';
import { IBookingObjects } from '@/shared/types/bookingObjects.types';
import { SliderButton } from '@/shared/uikit/components/button/sliderButton';
import { NetworkError } from '@/shared/uikit/components/error/networkError';

import { IconZoomIn, IconZoomOut, IconZoomReset } from '@tabler/icons-react';

import classes from './PlaceMap.module.css';

type PlaceMapProps = {
	parent: IBookingObjects;
	objectType: string;
	date?: TDateRange;
	isSingle?: boolean;
};

const zoomFactor = 100;

export const PlaceMap = ({ parent, objectType, date, isSingle = false }: PlaceMapProps) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const { bookingLocation, setBookingLocation } = useBookingStore();
	const targetPersonId = useUserStore((state) => state.targetPersonId);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLImageElement>(null);

	const [wrapperWidth, setWrapperWidth] = useState(1000);

	const { data: objectParents = null } = useQuery({
		queryKey: ['objectParents', parent],
		queryFn: () => bookingObjectsApi.getObjectParents(parent.location_id),
		enabled: !!parent && !isSingle,
	});

	const [image, status] = useImage(
		`${BASE_API_URL}${ApiRoutes.Objects}/${parent?.location_id || parent?.parent_id || parent?.id}/image/content` ??
			'',
	);

	useEffect(() => {
		if (wrapperRef.current?.offsetWidth) setWrapperWidth(wrapperRef.current?.offsetWidth);
	}, [wrapperRef.current]);

	if (!parent?.image_id && !image) {
		return null;
	}

	if (status === 'failed')
		return (
			<Flex align="center" justify="center" h="100%">
				<NetworkError />
			</Flex>
		);

	const items =
		objectParents &&
		getMembers([objectParents])?.map((item, index) => (
			<SliderButton
				key={index}
				active={item?.id !== bookingLocation?.id}
				{...(item?.id !== bookingLocation?.id ? { onClick: () => setBookingLocation(item) } : {})}
			>
				{item?.name}
			</SliderButton>
		));

	return (
		<>
			{items && isMobile && (
				<Carousel
					withControls={false}
					slidesToScroll="auto"
					slideSize="20%"
					slideGap={8}
					align="end"
					initialSlide={1}
					loop
					className={classes.carousel}
				>
					{items?.map((el) => <Carousel.Slide key={randomId()}>{el}</Carousel.Slide>)}
				</Carousel>
			)}

			<div className={classes.root} ref={wrapperRef}>
				<Skeleton visible={status === 'loading'}>
					{image && (
						<TransformWrapper
							initialScale={getImageScale(
								image.width,
								image.height,
								isMobile ? document.body.offsetWidth - 32 : wrapperWidth,
								isMobile ? 400 : 600,
							)}
							minScale={0.01}
							maxScale={zoomFactor}
							centerOnInit
							wheel={{ wheelDisabled: true }}
						>
							{({ zoomIn, zoomOut, resetTransform, ...rest }) => {
								return (
									<>
										{!isMobile && (
											<Flex align="center" justify="space-between">
												{items && <div className={classes.breadcrumbs}>{items}</div>}

												<Box mr={10}>
													<ActionIcon variant="transparent" onClick={() => zoomIn()}>
														<IconZoomIn />
													</ActionIcon>
													<ActionIcon variant="transparent" onClick={() => zoomOut()}>
														<IconZoomOut />
													</ActionIcon>
													<ActionIcon variant="transparent" onClick={() => resetTransform()}>
														<IconZoomReset />
													</ActionIcon>
												</Box>
											</Flex>
										)}

										<TransformComponent
											contentClass={classes.transformComponent}
											contentStyle={{
												transform: `translate3d(50%, 1px, 0px) scale(1)`,
											}}
											wrapperStyle={{
												width: '100%',
												height: isMobile ? '400px' : '600px',
											}}
										>
											{isSingle ? (
												<svg viewBox={`0 0 ${image?.width} ${image?.height}`}>
													<PlaceMapPath item={parent} personId={targetPersonId} isSingle />
												</svg>
											) : (
												//TODO: fix item types
												<svg viewBox={`0 0 ${image?.width} ${image?.height}`}>
													{parent?.booking_objects
														?.filter((object) =>
															objectType
																? object?.type.kind === objectType ||
																	object.type.kind === 'location'
																: object,
														)
														?.map((item) => (
															<PlaceMapPath
																key={item?.id}
																item={item}
																personId={targetPersonId}
																date={date}
															/>
														))}
												</svg>
											)}

											<img ref={contentRef} src={image?.src} alt="Схема" />
										</TransformComponent>
									</>
								);
							}}
						</TransformWrapper>
					)}
				</Skeleton>
			</div>
		</>
	);
};
